import React from 'react'
import {graphql, useFragment, useMutation} from 'react-relay/hooks'
import {useHover} from 'react-use'
import {InterpreterFrom} from 'xstate'
import {Button, ButtonProps} from '@chakra-ui/react'
import {useMachine, useActor} from '@xstate/react'
import {wrapEvent} from 'utils'

import {UserFollowButton_user$key} from './__generated__/UserFollowButton_user.graphql'
import {
  UserFollowButtonFollowMutation,
  UserFollowButtonFollowMutation$data,
} from './__generated__/UserFollowButtonFollowMutation.graphql'
import {
  UserFollowButtonUnfollowMutation,
  UserFollowButtonUnfollowMutation$data,
} from './__generated__/UserFollowButtonUnfollowMutation.graphql'

import {userFollowButtonMachine} from './userFollowButtonMachine'
import {ModalService} from 'components/ModalRoot'

// eslint-disable-next-line quotes
interface UserFollowButtonProps extends Omit<ButtonProps, 'children'> {
  user: UserFollowButton_user$key
}

export function UserFollowButton({
  user: _user,
  ...props
}: UserFollowButtonProps) {
  const user = useFragment(
    graphql`
      fragment UserFollowButton_user on User {
        id
        firstName
        lastName
        viewerIsFollowing
        followers(first: 1) {
          pageInfo {
            total
          }
        }
      }
    `,
    _user
  )
  const [followUserCommit] =
    useMutation<UserFollowButtonFollowMutation>(graphql`
      mutation UserFollowButtonFollowMutation($input: FollowUserInput!) {
        followUser(input: $input) {
          user {
            id
            viewerIsFollowing
            followers(first: 1) {
              pageInfo {
                total
              }
            }
          }
        }
      }
    `)

  const [unfollowUserCommit] =
    useMutation<UserFollowButtonUnfollowMutation>(graphql`
      mutation UserFollowButtonUnfollowMutation($input: UnfollowUserInput!) {
        unfollowUser(input: $input) {
          user {
            id
            viewerIsFollowing
            followers(first: 1) {
              pageInfo {
                total
              }
            }
          }
        }
      }
    `)
  const [, , userFollowButtonService] = useMachine(userFollowButtonMachine, {
    services: {
      followUser: (ctx, e) => {
        return new Promise<UserFollowButtonFollowMutation$data>(function (
          resolve,
          reject
        ) {
          followUserCommit({
            variables: {input: {userId: e.id}},
            onCompleted(response) {
              resolve(response)
            },
            onError(errors) {
              reject(errors)
            },
            optimisticResponse: {
              followUser: {
                user: {
                  id: e.id,
                  viewerIsFollowing: true,
                  followers: {
                    pageInfo: {
                      total: user.followers.pageInfo.total + 1,
                    },
                  },
                },
              },
            },
          })
        })
      },
      unfollowUser: (ctx, e) => {
        return new Promise<UserFollowButtonUnfollowMutation$data>(function (
          resolve,
          reject
        ) {
          unfollowUserCommit({
            variables: {input: {userId: e.id}},
            onCompleted(response) {
              resolve(response)
            },
            onError(errors) {
              reject(errors)
            },
            optimisticResponse: {
              unfollowUser: {
                user: {
                  id: e.id,
                  viewerIsFollowing: false,
                  followers: {
                    pageInfo: {
                      total: user.followers.pageInfo.total - 1,
                    },
                  },
                },
              },
            },
          })
        })
      },
    },
  })

  if (user.viewerIsFollowing) {
    return (
      <UnfollowButton
        {...props}
        userId={user.id}
        followerName={user.firstName}
        followerLastName={user.lastName}
        userFollowButtonService={userFollowButtonService}
      />
    )
  }
  
  return (
    <FollowButton
      {...props}
      userId={user.id}
      followerName={user.firstName}
      followerLastName={user.lastName}
      userFollowButtonService={userFollowButtonService}
    />
  )
}

// eslint-disable-next-line quotes
interface FollowButtonProps extends Omit<UserFollowButtonProps, 'user'> {
  userId: string
  followerName: string
  followerLastName: string
  userFollowButtonService: InterpreterFrom<typeof userFollowButtonMachine>
}

function FollowButton({
  onClick,
  userId,
  followerName,
  followerLastName,
  userFollowButtonService,
  ...props
}: FollowButtonProps) {
  const [, sendUserFollowButton] = useActor(userFollowButtonService)
  return (
    <Button
      variant="outline"
      colorScheme="green"
      onClick={(e) => {
        ModalService.send(`OPEN`, {
          modal: `FOLLOWING_CONFIRMATION_MODAL`,
          props: {
            title: `Follow ${followerName} ${followerLastName}`,
            description: `Confirm you'd like to follow ${followerName} ${followerLastName}?`,
            onConfirm: () => {
              wrapEvent(onClick, () => {
                sendUserFollowButton({type: 'FOLLOW_USER', id: userId})
              })(e)
            },
            buttonLabel: `Follow`,
          },
        })
      }}
      {...props}
    >
      Follow
    </Button>
  )
}

// eslint-disable-next-line quotes
interface UnfollowButtonProps extends Omit<UserFollowButtonProps, 'user'> {
  userId: string
  followerName: string
  followerLastName: string
  userFollowButtonService: InterpreterFrom<typeof userFollowButtonMachine>
}

function UnfollowButton({
  onClick,
  userId,
  followerName,
  followerLastName,
  userFollowButtonService,
  ...props
}: UnfollowButtonProps) {
  const [, sendUserFollowButton] = useActor(userFollowButtonService)
  const [element] = useHover((hovered) => (
    <Button
      colorScheme="green"
      _active={{bg: 'red.700'}}
      _hover={{bg: 'red.600'}}
      onClick={(e) => {
        ModalService.send(`OPEN`, {
          modal: `FOLLOWING_CONFIRMATION_MODAL`,
          props: {
            title: `Unfollow ${followerName} ${followerLastName}`,
            description: `Confirm you'd like to unfollow ${followerName} ${followerLastName}?`,
            onConfirm: () => {
              wrapEvent(onClick, () => {
                sendUserFollowButton({type: 'UNFOLLOW_USER', id: userId})
              })(e)
            },
            buttonLabel: 'Unfollow',
            isUnfollow: true
          },
        })
      }}
      {...props}
    >
      {hovered ? 'Unfollow' : 'Following'}
    </Button>
  ))

  return element
}
